import { enqueueSnackbar } from 'notistack';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';

import { MLInput } from 'components/Input/Input';
import { useCreateSiteContactForSite } from 'hooks/Clients/smartUsersForClientCreate/useCreateSiteContactForSite';

interface CreateSiteContactModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CreateSiteContactModal = ({ isOpen, onClose }: CreateSiteContactModalProps) => {
  const { createSiteContact, isLoading: isLoadingSiteContact } = useCreateSiteContactForSite();
  const { t } = useTranslation();

  const {
    handleSubmit: handleSiteContactSubmit,
    control: siteContactControl,
    reset: resetSiteContact,
    setError: setSiteContactError,
  } = useForm({
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      password: '',
      confirm_password: '',
    },
  });

  const onCloseModal = () => {
    onClose();
    resetSiteContact();
  };

  const onCreateSiteContact = (siteContactData: any) => {
    createSiteContact(siteContactData, {
      onSuccess: () => {
        enqueueSnackbar(t('Modals.manage-owner.siteContactSuccessCreate'), { variant: 'success' });
        onCloseModal();
      },
      onError: (error: any) => {
        handleSiteContactError(error);
      },
    });
  };

  const handleSiteContactError = (error: any) => {
    if (error.response?.data) {
      const errorData = error.response.data;

      // Handle field-specific errors
      if (errorData.first_name) {
        setSiteContactError('first_name', { message: errorData.first_name[0] });
      }
      if (errorData.last_name) {
        setSiteContactError('last_name', { message: errorData.last_name[0] });
      }
      if (errorData.email) {
        setSiteContactError('email', { message: errorData.email[0] });
      }
      if (errorData.phone) {
        setSiteContactError('phone', { message: errorData.phone[0] });
      }
      if (errorData.password) {
        setSiteContactError('password', { message: errorData.password[0] });
      }
      if (errorData.confirm_password) {
        setSiteContactError('confirm_password', { message: errorData.confirm_password[0] });
      }

      // Handle non-field errors (like password mismatch)
      if (errorData.non_field_errors) {
        setSiteContactError('confirm_password', { message: errorData.non_field_errors[0] });
        setSiteContactError('password', { message: errorData.non_field_errors[0] });
      }
    } else {
      enqueueSnackbar(t('Modals.manage-owner.failCreate'), { variant: 'error' });
    }
  };

  return (
    <div>
      <Modal
        isOpen={isOpen}
        toggle={() => {
          // onReset();
          onClose();
        }}
        size="lg"
        backdrop="static"
        keyboard={false}
      >
        <ModalHeader
          className="border-bottom py-3 mx-3"
          toggle={() => {
            // onReset();
            onClose();
          }}
        >
          <div className="d-flex justify-content-between align-items-center w-100 gap-5">
            <div className="">{t('Modals.manage-owner.createSiteContact')}</div>
          </div>
        </ModalHeader>
        <ModalBody>
          <div className="row">
            <div className="col-12">
              <h5 className="mb-3">{t('Modals.manage-owner.titleSiteContact')}</h5>
              <div className="row mb-3">
                <div className="col-md-6">
                  <Controller
                    control={siteContactControl}
                    name="first_name"
                    render={({ field, fieldState: { error } }) => (
                      <MLInput {...field} label={t('Modals.manage-owner.firstName')} error={error?.message} required />
                    )}
                  />
                </div>
                <div className="col-md-6">
                  <Controller
                    control={siteContactControl}
                    name="last_name"
                    render={({ field, fieldState: { error } }) => (
                      <MLInput {...field} label={t('Modals.manage-owner.lastName')} error={error?.message} required />
                    )}
                  />
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-md-6">
                  <Controller
                    control={siteContactControl}
                    name="email"
                    render={({ field, fieldState: { error } }) => (
                      <MLInput
                        {...field}
                        label={t('Modals.manage-owner.email')}
                        type="email"
                        error={error?.message}
                        required
                      />
                    )}
                  />
                </div>
                <div className="col-md-6">
                  <Controller
                    control={siteContactControl}
                    name="phone"
                    render={({ field, fieldState: { error } }) => (
                      <MLInput
                        {...field}
                        label={t('Modals.manage-owner.phone')}
                        type="phone"
                        error={error?.message}
                        required
                      />
                    )}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-md-6">
                  <Controller
                    control={siteContactControl}
                    name="password"
                    render={({ field, fieldState: { error } }) => (
                      <MLInput
                        {...field}
                        label={t('Modals.manage-owner.password')}
                        type="password"
                        error={error?.message}
                        required
                      />
                    )}
                  />
                </div>
                <div className="col-md-6">
                  <Controller
                    control={siteContactControl}
                    name="confirm_password"
                    render={({ field, fieldState: { error } }) => (
                      <MLInput
                        {...field}
                        label={t('Modals.manage-owner.confirmPassword')}
                        type="password"
                        error={error?.message}
                        required
                      />
                    )}
                  />
                </div>
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button className="btn btn-ghost-danger" onClick={onCloseModal}>
            {t('Modals.delete-modal.cancel')}
          </Button>
          <Button
            className="btn btn-ghost-success"
            onClick={handleSiteContactSubmit(onCreateSiteContact)}
            disabled={isLoadingSiteContact}
          >
            {isLoadingSiteContact ? t('Modals.manage-client.creating') : t('Modals.manage-owner.createSiteContact')}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default CreateSiteContactModal;
